import React, { useEffect, useRef, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Box,
    Typography,
    Button,
    TextField,
    Grid,
    IconButton
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import soundTherapyTracks from "./SoundTherapy.json";
import * as Icons from "@mui/icons-material";
import { getSmartBiometricSound } from "../../service/home";

const SoundTherapyDialogue = ({ open, onClose }) => {
    const [step, setStep] = useState(1);
    const [pulseCount, setPulseCount] = useState("");
    const [bpm, setBpm] = useState(null);
    const [answers, setAnswers] = useState({});
    const [showContinue, setShowContinue] = useState(false);
    const [selectedTracks, setSelectedTracks] = useState([]);
    const [selectedTrack, setSelectedTrack] = useState(null);
    const [audioUrl, setAudioUrl] = useState(null);
    const [loading, setLoading] = useState(false);
    const [isAudioCompleted, setIsAudioCompleted] = useState(false);
    const [openCompleteDialog, setOpenCompleteDialog] = useState(false);
    const [duration, setDuration] = useState(0);       // total seconds
    const [currentTime, setCurrentTime] = useState(0); // played seconds


    console.log("bpm", bpm)

    // Reset dialog when closed
    const handleClose = () => {
        setStep(1);
        setPulseCount("");
        setBpm(undefined);
        setAnswers({});
        setShowContinue(false); // ✅ ADD THIS
        setAssessmentStep(0);
        setAssessment({
            anxiety: null,
            depression: null,
            sleep: null,
            stress: null,
        });
        onClose();
    };

    const pulseValue = Number(pulseCount);
    const isValidPulse =
        pulseCount !== "" && pulseValue >= 10 && pulseValue <= 50;

    const [assessmentStep, setAssessmentStep] = useState(0); // 0–3

    const [assessment, setAssessment] = useState({
        anxiety: null,
        depression: null,
        sleep: null,
        stress: null,
    });

    const audioRef = useRef();

    useEffect(() => {
        if (step === 4 && audioRef.current) {
            audioRef.current.play().catch(err => console.log("Audio play blocked", err));
        }
    }, [step]);

    const handleReset = () => {
        setPulseCount("");
        setBpm(undefined);
    };

    const getStressLevel = (bpm) => {
        const value = Number(bpm);
        if (!value) return null;

        if (value >= 40 && value <= 75) return "low";
        if (value >= 76 && value <= 90) return "medium";
        if (value > 90) return "high";

        return null;
    };

    const getRandomTracks = (tracks, count = 3) => {
        return [...tracks]
            .sort(() => 0.5 - Math.random())
            .slice(0, count);
    };

    useEffect(() => {
        if (!bpm) return;

        const stressLevel = getStressLevel(bpm);
        const tracksForLevel = soundTherapyTracks[stressLevel] || [];

        const selectedTracks = getRandomTracks(tracksForLevel, 3);

        console.log("Stress Level:", stressLevel);
        console.log("Selected Music Tracks:", selectedTracks);
    }, [bpm]);

    useEffect(() => {
        if (!bpm) return;

        const level = getStressLevel(bpm);
        setSelectedTracks(getRandomTracks(soundTherapyTracks[level], 3));
    }, [bpm]);

    const formatTime = (seconds = 0) => {
        const m = Math.floor(seconds / 60);
        const s = Math.floor(seconds % 60);
        return `${m}:${s.toString().padStart(2, "0")}`;
    };

    return (
        <>
            <Dialog
                open={open}
                maxWidth="sm"
                fullWidth
                onClose={(event, reason) => {
                    // ❌ Block backdrop click & ESC
                    if (reason === "backdropClick" || reason === "escapeKeyDown") {
                        return;
                    }
                }}
            >
                {/* Header */}
                <DialogTitle>
                    <Typography
                        variant="h6"
                        fontWeight={600}
                        sx={{
                            display: 'inline-block',
                            background: 'linear-gradient(135deg, #22c55e, #14b8a6, #8b5cf6)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            color: 'transparent', // 🔑 important
                        }}
                    >
                        🎵 Smart Biometric Therapy
                    </Typography>
                    {/* {step !== 4 && (
                        <IconButton
                            onClick={handleClose}
                            sx={{ position: "absolute", right: 8, top: 8 }}
                        >
                            <CloseIcon />
                        </IconButton>
                    )} */}

                    {/* {step === 4 && (
                        <Button
                            variant="text"
                            size="small"
                            sx={{ position: "absolute", right: 8, top: 8, mt: 1, color: "#7843f3" }}
                            onClick={() => setStep(3)}
                        >
                            Back
                        </Button>
                    )} */}
                </DialogTitle>

                <DialogContent>

                    {/* STEP 1 – Manual Pulse Entry (Default Method) */}
                    {step === 1 && (
                        <Box>
                            {/* Title */}
                            <Typography fontWeight={600} sx={{ mb: 0.5, color: "#7843f3" }}>
                                Manual Pulse Entry
                            </Typography>

                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Enter your pulse count (15 seconds):
                            </Typography>

                            {/* Input */}
                            <TextField
                                fullWidth
                                type="number"
                                value={pulseCount}
                                onChange={(e) => {
                                    setPulseCount(e.target.value);
                                    setBpm(undefined);
                                }}
                                placeholder="e.g. 18"
                                inputProps={{ min: 10, max: 50 }}
                                error={pulseCount !== "" && !isValidPulse}
                            />

                            {/* Validation Message */}
                            {!isValidPulse && pulseCount !== "" && (
                                <Typography
                                    variant="body2"
                                    color="error"
                                    sx={{ mt: 1 }}
                                >
                                    Please enter a value between 10–50 beats
                                </Typography>
                            )}

                            {/* BPM Result */}
                            {bpm && (
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        mt: 2
                                    }}
                                >
                                    <Typography variant="body2" fontWeight={600}>
                                        Your heart rate: {bpm} BPM
                                    </Typography>

                                    <Button
                                        variant="outlined"
                                        size="small"
                                        sx={{ color: "#7843f3" }}
                                        onClick={handleReset}
                                    >
                                        Reset
                                    </Button>
                                </Box>
                            )}

                            {/* Calculate Button */}
                            <Button
                                variant="contained"
                                sx={{
                                    mt: 3,
                                    mx: 'auto',          // 👈 centers horizontally
                                    display: 'block',    // required for mx auto
                                    background: 'linear-gradient(135deg, #22c55e, #14b8a6, #8b5cf6)',
                                }}
                                disabled={!bpm && !isValidPulse}
                                onClick={() => {
                                    if (!bpm) {
                                        const calculatedBpm = pulseValue * 4;
                                        setBpm(calculatedBpm);

                                        alert(
                                            `Heart rate calculated\n` +
                                            `Pulse count : ${pulseValue} beats in 15 sec\n` +
                                            `Heart rate - ${calculatedBpm} BPM`
                                        );

                                        setShowContinue(true);
                                    } else {
                                        setStep(2);
                                    }
                                }}
                            >
                                {bpm ? 'Continue to Assessment' : 'Calculate Heart Rate'}
                            </Button>


                            {/* How to Measure Box (already fixed earlier) */}
                            <Box
                                sx={{
                                    mt: 3,
                                    p: 2,
                                    border: "1px solid #e0e0e0",
                                    borderRadius: 2,
                                    bgcolor: "#fafafa"
                                }}
                            >
                                <Typography variant="body1" fontWeight={600} sx={{ mb: 1 }}>
                                    📋 How to Measure:
                                </Typography>

                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    <strong>Method 1: Manual Pulse Count</strong>
                                    <br />
                                    1. Find your pulse on your wrist (radial artery) or neck (carotid artery)
                                    <br />
                                    2. Use a timer and count beats for exactly 15 seconds
                                    <br />
                                    3. Enter the number of beats you counted above
                                    <br />
                                    4. We'll calculate your BPM (beats × 4)
                                </Typography>

                                <Typography variant="body2">
                                    <strong>Example:</strong> 18 beats in 15 seconds = 72 BPM
                                </Typography>
                            </Box>

                            {/* {showContinue && (
                            <Button
                                fullWidth
                                variant="outlined"
                                sx={{ mt: 2 }}
                                onClick={() => setStep(2)}
                            >
                                Continue to Assessment
                            </Button>
                        )} */}
                        </Box>
                    )}


                    {/* STEP 2 – Wellness Assessment */}
                    {step === 2 && (
                        <Box>
                            <Typography fontWeight={600} mb={2} sx={{ color: "#7843f3" }}>
                                Wellness Assessment
                            </Typography>

                            {/* QUESTIONS CONFIG */}
                            {[
                                {
                                    key: "anxiety",
                                    question: "How would you describe your anxiety level today?",
                                    options: [
                                        { value: 1, emoji: "😌", label: "Calm & Peaceful" },
                                        { value: 2, emoji: "😐", label: "Slightly Anxious" },
                                        { value: 3, emoji: "😟", label: "Moderately Anxious" },
                                        { value: 4, emoji: "😰", label: "Highly Anxious" },
                                    ],
                                },
                                {
                                    key: "depression",
                                    question: "How has your mood been?",
                                    options: [
                                        { value: 1, emoji: "😊", label: "Happy & Energetic" },
                                        { value: 2, emoji: "😕", label: "Slightly Down" },
                                        { value: 3, emoji: "😔", label: "Moderately Low" },
                                        { value: 4, emoji: "😢", label: "Very Low" },
                                    ],
                                },
                                {
                                    key: "sleep",
                                    question: "Sleep quality recently?",
                                    options: [
                                        { value: 1, emoji: "😴", label: "Excellent" },
                                        { value: 2, emoji: "😪", label: "Fair" },
                                        { value: 3, emoji: "🥱", label: "Poor" },
                                        { value: 4, emoji: "😫", label: "Very Poor" },
                                    ],
                                },
                                {
                                    key: "stress",
                                    question: "Current stress level?",
                                    options: [
                                        { value: 1, emoji: "🧘", label: "Minimal" },
                                        { value: 2, emoji: "😅", label: "Moderate" },
                                        { value: 3, emoji: "😓", label: "High" },
                                        { value: 4, emoji: "🤯", label: "Extreme" },
                                    ],
                                },
                            ].map((q, index) =>
                                assessmentStep === index ? (
                                    <Box key={q.key}>
                                        {/* Question */}
                                        <Typography fontWeight={600} mb={2}>
                                            {q.question}
                                        </Typography>

                                        {/* Options */}
                                        <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                                            {q.options.map((opt) => (
                                                <Grid
                                                    item
                                                    key={opt.value}
                                                    xs={12}        // 📱 Mobile → full width
                                                    sm={6}         // 💻 Tablet+ → 2 columns
                                                >
                                                    <Button
                                                        fullWidth
                                                        variant={assessment[q.key] === opt.value ? "contained" : "outlined"}
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: {
                                                                xs: 'center',   // 📱 center on mobile
                                                                sm: 'flex-start',
                                                            },
                                                            gap: 1,
                                                            py: {
                                                                xs: 1.2,
                                                                sm: 1.5,
                                                            },
                                                            px: {
                                                                xs: 1.5,
                                                                sm: 2,
                                                            },
                                                            fontWeight: 600,
                                                            fontSize: {
                                                                xs: '0.85rem',
                                                                sm: '0.95rem',
                                                            },

                                                            // 🎨 base color
                                                            color: assessment[q.key] === opt.value ? '#fff' : '#7843f3',
                                                            borderColor: '#7843f3',

                                                            // 🎯 selected state
                                                            backgroundColor:
                                                                assessment[q.key] === opt.value ? '#7843f3' : 'transparent',

                                                            // 🖱 hover
                                                            '&:hover': {
                                                                backgroundColor:
                                                                    assessment[q.key] === opt.value
                                                                        ? '#6a38e0'
                                                                        : 'rgba(120, 67, 243, 0.08)',
                                                                borderColor: '#7843f3',
                                                            },
                                                        }}
                                                        onClick={() =>
                                                            setAssessment((prev) => ({
                                                                ...prev,
                                                                [q.key]: opt.value,
                                                            }))
                                                        }
                                                    >
                                                        <Box
                                                            component="span"
                                                            sx={{
                                                                fontSize: { xs: 18, sm: 20 },
                                                                lineHeight: 1,
                                                            }}
                                                        >
                                                            {opt.emoji}
                                                        </Box>

                                                        <Box
                                                            component="span"
                                                            sx={{
                                                                whiteSpace: 'normal',
                                                                textAlign: {
                                                                    xs: 'center',
                                                                    sm: 'left',
                                                                },
                                                            }}
                                                        >
                                                            {opt.label}
                                                        </Box>
                                                    </Button>
                                                </Grid>
                                            ))}
                                        </Grid>

                                        {/* Next Button */}
                                        <Button
                                            sx={{
                                                mt: 3,
                                                mx: 'auto',          // 👈 centers horizontally
                                                display: 'block',    // required for mx auto
                                                background: 'linear-gradient(135deg, #22c55e, #14b8a6, #8b5cf6)',
                                                color: assessment[q.key] ? '#fff' : '#9e9e9e', // 👈 text color
                                                '&.Mui-disabled': {
                                                    color: '#9e9e9e', // disabled text color
                                                },
                                            }}
                                            disabled={!assessment[q.key]}
                                            onClick={() => {
                                                if (assessmentStep < 3) {
                                                    setAssessmentStep((prev) => prev + 1);
                                                } else {
                                                    setStep(3); // move to Wellness Profile
                                                }
                                            }}
                                        >
                                            {assessmentStep === 3
                                                ? "Get Recommendations →"
                                                : "Next →"}
                                        </Button>
                                    </Box>
                                ) : null
                            )}
                        </Box>
                    )}

                    {/* STEP 3 – Your Wellness Profile */}
                    {step === 3 && (
                        <Box>
                            <Box
                                sx={{
                                    background: "linear-gradient(135deg, #f0f4ff 0%, #e6f2ff 100%)",
                                    borderRadius: 2,
                                    p: 3,
                                    mb: 3
                                }}
                            >
                                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                                    <Box
                                        sx={{
                                            width: 50,
                                            height: 50,
                                            borderRadius: 1.5,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            fontSize: 24,
                                            color: "white",
                                            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                                        }}
                                    >
                                        🧠
                                    </Box>
                                    <Box>
                                        <Typography variant="h6" sx={{ color: "#7843f3" }}>Your Wellness Profile</Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Analysis Complete
                                        </Typography>
                                    </Box>
                                </Box>

                                <Box
                                    sx={{
                                        display: "grid",
                                        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                                        gap: 2,
                                        mb: 2
                                    }}
                                >
                                    <Box
                                        sx={{
                                            background: "white",
                                            borderRadius: 1.5,
                                            p: 2,
                                            textAlign: "center",
                                            boxShadow: "0 2px 8px rgba(0,0,0,0.05)"
                                        }}
                                    >
                                        <Typography variant="caption" color="text.secondary">
                                            Heart Rate
                                        </Typography>
                                        <Typography variant="h4" sx={{ fontWeight: 700, color: "#7843f3" }}>
                                            {bpm} BPM
                                        </Typography>
                                    </Box>

                                    <Box
                                        sx={{
                                            background: "white",
                                            borderRadius: 1.5,
                                            p: 2,
                                            textAlign: "center",
                                            boxShadow: "0 2px 8px rgba(0,0,0,0.05)"
                                        }}
                                    >
                                        <Typography variant="caption" color="text.secondary">
                                            Stress Level
                                        </Typography>
                                        <Typography variant="h4" sx={{ fontWeight: 700, color: "#7843f3" }}>
                                            {bpm == null
                                                ? "--"
                                                : bpm <= 75
                                                    ? "Low"
                                                    : bpm <= 90
                                                        ? "Medium"
                                                        : "High"}
                                        </Typography>
                                    </Box>
                                </Box>

                                <Box
                                    sx={{
                                        background: "white",
                                        borderRadius: 1.5,
                                        p: 2,
                                        borderLeft: "4px solid #667eea"
                                    }}
                                >
                                    <Typography variant="subtitle1" sx={{ mb: 1 }}>
                                        💡 echo recommendation
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Based on your heart rate and stress level, we recommend therapeutic
                                        music sessions to help you relax and restore balance.
                                    </Typography>
                                </Box>
                            </Box>

                            {/* Personalized Tracks */}
                            <Box>
                                <Typography
                                    variant="h5"
                                    sx={{
                                        mb: 1,
                                        fontWeight: 600,
                                        display: 'inline-block',
                                        background: 'linear-gradient(135deg, #22c55e, #14b8a6, #8b5cf6)',
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: 'transparent',
                                        color: 'transparent', // 🔑 important
                                    }}
                                >
                                    🎵 Your Personalized Tracks
                                </Typography>

                                {/* Description */}
                                {/* <Typography
                                    variant="body2"
                                    sx={{
                                        mb: 1,
                                        color: "#4a5568",
                                        fontSize: "0.875rem",   // smaller text
                                        lineHeight: 2,          // more gap between lines
                                        maxWidth: 700
                                    }}
                                >
                                    Choose any track that resonates with you.
                                </Typography> */}
                                {/* <Typography
                                    variant="body2"
                                    sx={{
                                        mb: 1,
                                        color: "#4a5568",
                                        fontSize: "0.875rem",   // smaller text
                                        lineHeight: 2,          // more gap between lines
                                        maxWidth: 700
                                    }}
                                >
                                    Put on your headphones, find a calm and comfortable place,
                                    and allow yourself 30 minutes of uninterrupted listening.
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        mb: 1,
                                        color: "#4a5568",
                                        fontSize: "0.875rem",   // smaller text
                                        lineHeight: 2,          // more gap between lines
                                        maxWidth: 700
                                    }}
                                >
                                    Let the sounds gently guide you into a state of deep relaxation,
                                    soothing calm, and inner balance.
                                </Typography> */}
                                <Typography
                                    variant="body2"
                                    sx={{
                                        mb: 1,
                                        color: "#4a5568",
                                        fontSize: "0.875rem",   // smaller text
                                        lineHeight: 2,          // more gap between lines
                                        maxWidth: 700
                                    }}
                                >
                                    Simply listen. Simply breathe.
                                </Typography>


                                <Box
                                    sx={{
                                        display: "grid",
                                        gap: 2
                                    }}
                                >
                                    {selectedTracks.map((track, i) => {
                                        const isSelected = selectedTrack?.title === track.title;

                                        const IconComponent =
                                            Icons[track.icon] || Icons.MusicNote;

                                        return (
                                            <Box
                                                key={i}
                                                sx={{
                                                    background: 'linear-gradient(135deg, #f8fffe 0%, #f5f3ff 100%)', // ✅ gradient background
                                                    border: `2px solid ${isSelected ? '#7843f3' : '#e2e8f0'}`,
                                                    borderRadius: 2,
                                                    p: 2,
                                                    cursor: 'pointer',
                                                    transition: 'all 0.3s ease',
                                                    boxShadow: isSelected
                                                        ? '0 8px 20px rgba(120,67,243,0.25)'
                                                        : '0 2px 8px rgba(0,0,0,0.05)',
                                                    '&:hover': {
                                                        transform: 'translateX(5px)',
                                                        borderColor: '#7843f3',
                                                    },
                                                }}
                                                onClick={() => setSelectedTrack(track)}
                                            >
                                                {/* Top Row */}
                                                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                                                    <IconComponent
                                                        fontSize="large"
                                                        sx={{ color: "#667eea" }}   // blue
                                                    />

                                                    <Typography
                                                        sx={{
                                                            fontSize: 12,
                                                            fontWeight: 600,
                                                            color: "white",
                                                            background: "#667eea",
                                                            px: 1.5,
                                                            height: 26,
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            borderRadius: 2,
                                                            whiteSpace: "nowrap"
                                                        }}
                                                    >
                                                        {Math.floor(track.duration / 60)} min
                                                    </Typography>
                                                </Box>

                                                {/* Title */}
                                                <Typography
                                                    sx={{ fontSize: 18, fontWeight: 600, color: "#2d3748", mb: 0.5 }}
                                                >
                                                    {track.title}
                                                </Typography>

                                                {/* Description */}
                                                <Typography sx={{ fontSize: 14, color: "#718096" }}>
                                                    {track.subtitle1}
                                                </Typography>

                                                {/* Tag */}
                                                <Box sx={{ mt: 1 }}>
                                                    <Typography
                                                        sx={{
                                                            fontSize: 12,
                                                            color: "#667eea",
                                                            display: "inline-block",
                                                            background: "rgba(102,126,234,0.1)",
                                                            px: 1,
                                                            borderRadius: 1
                                                        }}
                                                    >
                                                        {track.subtitle2}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        );
                                    })}
                                </Box>
                            </Box>

                            <Button
                                variant="contained"
                                sx={{
                                    mt: 3,
                                    mx: 'auto',          // 👈 centers horizontally
                                    display: 'block', background: 'linear-gradient(135deg, #22c55e, #14b8a6, #8b5cf6)',
                                }}
                                disabled={!selectedTrack || loading}
                                onClick={async () => {
                                    if (!selectedTrack?.trackId) return;

                                    try {
                                        setLoading(true);

                                        const data = await getSmartBiometricSound(selectedTrack.trackId);

                                        // store signed url
                                        setAudioUrl(data.track_url);

                                        // move to audio player
                                        setStep(4);
                                    } catch (err) {
                                        console.error(err);
                                    } finally {
                                        setLoading(false);
                                    }
                                }}
                            >
                                {loading ? "Loading..." : "Play Track →"}
                            </Button>

                        </Box>
                    )}

                    {/* STEP 4 – Therapy Music Player */}
                    {step === 4 && (
                        <Box className="audio-player">

                            <Box className="player-header" sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                                <Box
                                    sx={{
                                        width: 60,
                                        height: 60,
                                        borderRadius: 2,
                                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: 28,
                                        color: 'white',
                                    }}
                                >
                                    🎵
                                </Box>
                                <Box>
                                    <Typography variant="h6" sx={{ color: "#7843f3" }}>{selectedTrack?.title}</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        echo Therapeutic Session
                                    </Typography>
                                </Box>
                            </Box>



                            <Box className="live-bio-display" sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, mb: 3 }}>
                                <Box className="live-bio-card" sx={{ textAlign: 'center', p: 2, bgcolor: 'rgba(0,0,0,0.05)', borderRadius: 1 }}>
                                    <Typography className="live-bio-icon">❤️</Typography>
                                    <Typography className="live-bio-value" sx={{ color: "#7843f3" }}>{bpm || "--"}</Typography>
                                    <Typography className="live-bio-label" sx={{ color: "#7843f3" }}>Heart Rate</Typography>
                                </Box>
                                <Box
                                    className="live-bio-card"
                                    sx={{
                                        textAlign: 'center',
                                        p: 2,
                                        bgcolor: 'rgba(0,0,0,0.05)',
                                        borderRadius: 1
                                    }}
                                >
                                    <Typography className="live-bio-icon">📊</Typography>

                                    <Typography className="live-bio-value" sx={{ color: "#7843f3" }}>
                                        {bpm == null
                                            ? "--"
                                            : bpm <= 75
                                                ? "Low"
                                                : bpm <= 90
                                                    ? "Medium"
                                                    : "High"}
                                    </Typography>


                                    <Typography className="live-bio-label" sx={{ color: "#7843f3" }}>Stress</Typography>
                                </Box>

                            </Box>

                            <audio
                                ref={audioRef}
                                src={audioUrl}
                                autoPlay
                                preload="auto"
                                style={{ display: "none" }} // 🔥 hide native UI

                                // ⛔ block pause
                                onPause={(e) => {
                                    e.target.play().catch(() => { });
                                }}

                                // ⛔ block seeking
                                onSeeking={(e) => {
                                    e.target.currentTime = currentTime;
                                }}

                                onLoadedMetadata={(e) => {
                                    setDuration(e.target.duration);
                                }}

                                onTimeUpdate={(e) => {
                                    setCurrentTime(e.target.currentTime);
                                }}

                                onEnded={() => setIsAudioCompleted(true)}
                            />

                            <Box sx={{ mt: 2 }}>
                                {/* Progress info */}
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        mb: 0.5,
                                    }}
                                >
                                    <Typography variant="caption" color="text.secondary">
                                        {formatTime(currentTime)}
                                    </Typography>

                                    <Typography variant="caption" color="text.secondary">
                                        {formatTime(duration)}
                                    </Typography>
                                </Box>

                                {/* Read-only progress bar */}
                                <Box
                                    sx={{
                                        height: 8,
                                        borderRadius: 4,
                                        backgroundColor: "#e5e7eb",
                                        overflow: "hidden",
                                    }}
                                >
                                    <Box
                                        sx={{
                                            height: "100%",
                                            width: `${(currentTime / duration) * 100 || 0}%`,
                                            background: "linear-gradient(135deg, #22c55e, #14b8a6, #8b5cf6)",
                                            transition: "width 0.25s linear",
                                        }}
                                    />
                                </Box>
                            </Box>

                            {isAudioCompleted && (<Button
                                variant="contained"
                                sx={{
                                    mt: 3,
                                    mx: 'auto',          // 👈 centers horizontally
                                    display: 'block', background: 'linear-gradient(135deg, #22c55e, #14b8a6, #8b5cf6)',
                                }}
                                onClick={() => setOpenCompleteDialog(true)}
                            >
                                Complete Session
                            </Button>)}
                        </Box>
                    )}


                </DialogContent>
            </Dialog>
            <Dialog
                open={openCompleteDialog}
                onClose={() => {
                    setOpenCompleteDialog(false);
                    handleClose();
                }}
                maxWidth="xs"
                fullWidth
            >
                <DialogContent sx={{ textAlign: "center", p: 4 }}>
                    <Typography variant="h6" fontWeight={600} gutterBottom sx={{ color: "#7843f3" }}>
                        ✅ Session Completed
                    </Typography>

                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Be with Echo for 30 minutes a day and build a healthy habit for managing stress.
                    </Typography>

                    <Button
                        variant="contained"
                        sx={{
                            mt: 3, mx: 'auto',          // 👈 centers horizontally
                            display: 'block', background: 'linear-gradient(135deg, #22c55e, #14b8a6, #8b5cf6)',
                        }}
                        onClick={() => {
                            setOpenCompleteDialog(false);
                            handleClose();
                        }}
                    >
                        Done
                    </Button>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default SoundTherapyDialogue;
